import type { CampaignTheme, CheckoutQueryParams } from '@classy/campaign-page-blocks'
import type { Block, SharedBlock } from 'features/Block/block.model'
import type { TransactionSummary } from 'features/Block/transaction-details/transaction-details.model'
import type { OrganizationChannel } from 'services/analytics'
import type { OrderSummaryDto } from './orders'
import type { AnalyticsServiceSettingsDto, CampaignType } from './studio'
import type { DesignationDto } from './designations'
import type { OptimizelyContextType } from './optimizely'

/**
 * Interfaces/types/constants for page-related information.
 */

export const PAGE_TYPES = {
  LANDING: 'landing',
  DONATION: 'donation',
  THANK_YOU: 'thank-you',
  TEAM: 'team',
  FUNDRAISER: 'fundraiser',
} as const

export type PageType = (typeof PAGE_TYPES)[keyof typeof PAGE_TYPES]

/**
 * The shape of a campaign page with its "layout" and "blocks" objects combined.
 */
export interface DenormalizedPageData {
  campaign_id: string
  theme: CampaignTheme
  sharedBlocks: Record<string, SharedBlock>
  pageData: Block
}

/**
 * Whammy data necessary to render the page.
 *
 * Generated in getServerSideProps().
 * Used by DonationPage, ThankYouPage, etc.
 */
export interface PageConfig {
  analyticsServiceSettings?: AnalyticsServiceSettingsDto[]
  campaignId: string
  campaignName: string
  campaignCreatedAt: string
  campaignLocale: string
  campaignRawCurrencyCode: string
  campaignRawGoal: number
  customEmbeddedCodeDomain?: string
  isCustomEmbeddedCodeEnabled?: boolean
  organizationChannels?: OrganizationChannel[]
  checkoutBaseUrl?: string
  currentDomain?: string
  defaultDesignationId: number
  forwardCheckoutQueryParams?: CheckoutQueryParams
  optimizelyContext?: OptimizelyContextType
  isFloatingDonateButtonActive?: boolean
  /**
   * This covers both legacy and new Intelligent Ask Amount (IAA).
   *
   * Legacy IAA = studio campaign: campaign_data.use_intelligent_ask_*
   * New IAA = studio campaign: intelligent_asks.is_enabled
   *
   * TODO: CL-48068, merge onetimeEnabled and recurringEnabled into isEnabled
   */
  intelligentAskConfig?: {
    onetimeEnabled?: boolean
    recurringEnabled?: boolean
    // If preview page, fetch IAA config from latest version (not latest published version)
    isPreviewPage?: boolean
  }
  isCartEnabled?: boolean
  isPassportDisabled?: boolean
  openGraphTagFacebookText?: string
  openGraphTagFacebookImageUrl?: string
  orgDomainMaskFavicon?: string
  orgId: string
  orgName: string
  orgOptimizelyProjectId?: string
  pagePathname?: string
  pageTitle: string
  transaction?: TransactionSummary | OrderSummaryDto
  designations?: { [key: string]: DesignationDto }
  transactionDetails?: TransactionSummary
  purchaseDetails?: TransactionSummary
  blockCount?: number
  campaignType?: CampaignType
}
